import type { ColorPalette } from '../types/theme';
import type { FigmaColorScale, FigmaColorScaleName, FigmaColorTone, SemanticColors } from './types';

export type { FigmaColorScale, FigmaColorScaleName, FigmaColorTone, SemanticColors, SemanticColorTones } from './types';

const white = '#FFFFFF';

export const figmaColorScaleOrder: readonly FigmaColorTone[] = [
  1000,
  900,
  800,
  700,
  600,
  500,
  400,
  300,
  200,
  100,
  50,
] as const;

// Build a tone->hex scale from hexes given darkest -> lightest (matching figmaColorScaleOrder).
const scale = (...hexes: string[]): FigmaColorScale => Object.fromEntries(figmaColorScaleOrder.map((tone, i) => [tone, hexes[i]])) as FigmaColorScale;

export const figmaColorScales: Record<FigmaColorScaleName, FigmaColorScale> = {
  green: scale('#0A0F07', '#183020', '#244A32', '#336648', '#407E58', '#4E9669', '#72AE87', '#98C4A6', '#BDDBC8', '#DAEEE0', '#EDF7F1'),
  blue: scale('#060D16', '#16263A', '#203C58', '#2E5472', '#3A6A8E', '#4A7EA8', '#6E98BC', '#96B4CC', '#BAD0E0', '#D8E6EF', '#EBF3F8'),
  purple: scale('#120828', '#220F4E', '#361970', '#4E2A98', '#6E42B8', '#8B5DD4', '#A880DF', '#C4A8EA', '#DCCAF3', '#EDE3F9', '#F6F0FC'),
  orange: scale('#100600', '#381C08', '#582C10', '#7C3E1C', '#9E4E24', '#C06030', '#D08058', '#DFA088', '#ECC0AC', '#F6DDD2', '#FBF0EA'),
  red: scale('#0E0101', '#340404', '#540808', '#7A1010', '#A01C1C', '#CC2828', '#DE5858', '#EC8888', '#F6B4B4', '#FCDCDC', '#FEEEEE'),
  yellow: scale('#0E0C00', '#2A2000', '#503C00', '#6A5200', '#9A7800', '#F0D040', '#F4DC6C', '#FFE898', '#FFF3B0', '#FFFAE0', '#FDFDF5'),
  neutral: scale('#100F0A', '#1D1D15', '#333028', '#504C3E', '#6C6858', '#888070', '#A8A090', '#C4BEB0', '#DAD5CB', '#EDE9E1', '#F8F5F0'),
};

const { green, blue, purple, orange, red, yellow, neutral } = figmaColorScales;

export const figmaMainColors = {
  primarySuccessCloseOut: green[500],
  secondaryInfoPunchList: blue[500],
  accentScope: purple[500],
  accentWarningChangeOrder: orange[500],
  accentError: red[500],
  accentBrand: yellow[500],
  neutral: neutral[500],
  white,
} as const;

const intentScales = {
  success: green,
  info: blue,
  scope: purple,
  warning: orange,
  error: red,
  brand: yellow,
} as const;

// Build the six semantic intents from one (strong, default, subtle) tone triple.
const intents = (
  strong: FigmaColorTone,
  base: FigmaColorTone,
  subtle: FigmaColorTone,
): SemanticColors['intent'] =>
  Object.fromEntries(
    Object.entries(intentScales).map(([name, s]) => [
      name,
      { strong: s[strong], default: s[base], subtle: s[subtle] },
    ]),
  ) as SemanticColors['intent'];

export const figmaSemanticColors: SemanticColors = {
  text: { primary: neutral[900], secondary: neutral[700], tertiary: neutral[500], inverse: white },
  surface: { canvas: white, subtle: neutral[50], muted: neutral[100] },
  border: { subtle: neutral[200], default: neutral[300], strong: neutral[500] },
  intent: intents(600, 500, 200),
};

export const figmaSemanticColorsDark: SemanticColors = {
  text: { primary: neutral[50], secondary: neutral[300], tertiary: neutral[400], inverse: neutral[1000] },
  surface: { canvas: neutral[1000], subtle: neutral[900], muted: neutral[800] },
  border: { subtle: neutral[800], default: neutral[700], strong: neutral[500] },
  intent: intents(400, 500, 800),
};

export const colors: ColorPalette = {
  black: neutral[1000],
  offBlack: neutral[900],
  charcoal: neutral[900],
  darkestGray: neutral[800],
  darkerGray: neutral[700],
  darkGray: neutral[500],
  gray: neutral[300],
  lightGray: neutral[200],
  lightestGray: neutral[100],
  offWhite: neutral[50],
  white,

  veryDarkBlue: blue[1000],
  darkBlue: blue[600],
  blue: blue[500],
  lightBlue: blue[400],
  offWhiteBlue: blue[50],

  veryDarkGreen: green[1000],
  darkGreen: green[600],
  green: green[500],
  lightGreen: green[400],
  offWhiteGreen: green[50],

  veryDarkPurple: purple[1000],
  darkPurple: purple[600],
  purple: purple[500],
  lightPurple: purple[400],
  offWhitePurple: purple[50],

  veryDarkYellow: yellow[1000],
  darkYellow: yellow[600],
  yellow: yellow[500],
  lightYellow: yellow[400],
  offWhiteYellow: yellow[50],

  veryDarkRed: red[1000],
  darkRed: red[600],
  red: red[500],
  lightRed: red[400],
  offWhiteRed: red[50],

  veryDarkOrange: orange[1000],
  darkOrange: orange[600],
  orange: orange[500],
  lightOrange: orange[400],
  offWhiteOrange: orange[50],
};
